<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CaptainAssistantAPI from 'dashboard/api/captain/assistant';
import AcoesPasso from '../components/AcoesPasso.vue';
import DicaDestaque from '../components/DicaDestaque.vue';
import { BTN, BTN_SECONDARY, BTN_SMALL, CARD, CHIP, CHIP_TONES, ENTER, FIELD, H1, SMALL, SUB, TILE, TILE_TONES } from '../ui';

// Teste da IA como comprador pelo playground do assistente: ida e volta
// simulada, nada sai para cliente nenhum.
const props = defineProps({
  state: { type: Object, required: true },
  accountId: { type: [Number, String], required: true },
});
const emit = defineEmits(['next']);

const { t } = useI18n();
const assistant = ref(null);
const loadingAssistant = ref(true);
const messages = ref([]);
const draft = ref('');
const thinking = ref(false);
const conversation = ref(null);

const facts = computed(() => props.state.facts || {});
const brandColor = computed(() => facts.value.brand_color?.value || null);
const answered = computed(() =>
  messages.value.some(m => m.role === 'assistant' && !m.error)
);
const toolsUsed = computed(() => [
  ...new Set(
    messages.value.flatMap(m =>
      (m.toolCalls || []).map(call => call.name || call.function?.name)
    )
  ),
].filter(Boolean));

const SUGGESTIONS = ['VISIT', 'FINANCING', 'GARAGE'];

onMounted(async () => {
  try {
    const { data } = await CaptainAssistantAPI.get();
    assistant.value = data?.payload?.[0] || null;
  } catch {
    assistant.value = null;
  } finally {
    loadingAssistant.value = false;
  }
});

const scrollDown = async () => {
  await nextTick();
  conversation.value?.scrollTo?.({ top: conversation.value.scrollHeight, behavior: 'smooth' });
};

const history = () =>
  messages.value
    .filter(m => !m.error)
    .map(m => ({ role: m.role, content: m.content }));

const send = async text => {
  const content = (text ?? draft.value).trim();
  if (!content || thinking.value || !assistant.value) return;
  draft.value = '';
  messages.value.push({ role: 'user', content });
  thinking.value = true;
  scrollDown();
  try {
    const { data } = await CaptainAssistantAPI.playground({
      assistantId: assistant.value.id,
      messageContent: content,
      messageHistory: history(),
    });
    messages.value.push({
      role: 'assistant',
      content: data.response,
      handoff: data.response === 'conversation_handoff',
      toolCalls: data.tool_calls || [],
    });
  } catch {
    messages.value.push({ role: 'assistant', error: true, content: t('ONBOARDING_VISOR.AI_TEST.ERROR') });
  } finally {
    thinking.value = false;
    scrollDown();
  }
};

const restart = () => {
  messages.value = [];
};

const tips = computed(() => [
  t('ONBOARDING_VISOR.AI_TEST.TIPS.CORRECT'),
  t('ONBOARDING_VISOR.AI_TEST.TIPS.VOICE'),
]);
</script>

<template>
  <div>
    <section :class="ENTER">
      <h1 :class="H1">
        <span class="text-n-slate-10">{{ t('ONBOARDING_VISOR.AI_TEST.HELLO') }}</span>
        <br />
        {{ t('ONBOARDING_VISOR.AI_TEST.TITLE') }}
      </h1>
      <p :class="SUB">{{ t('ONBOARDING_VISOR.AI_TEST.SUBTITLE') }}</p>
    </section>

    <div class="grid gap-6 mt-8 lg:grid-cols-[1.25fr_1fr] items-start max-md:gap-4 max-md:mt-5">
      <section :class="CARD" class="!p-0 overflow-hidden flex flex-col" data-testid="chat">
        <div class="flex items-center gap-3 px-5 py-4 border-b border-n-weak">
          <img v-if="facts.logo_url?.value" :src="facts.logo_url.value" alt="" class="size-9 rounded-lg object-contain bg-white" />
          <span v-else :class="[TILE, TILE_TONES.azul]"><Icon icon="i-ph-storefront-duotone" /></span>
          <div>
            <b class="font-medium">{{ facts.name?.value }}</b>
            <p class="flex items-center gap-1.5" :class="SMALL">
              <i class="size-2 rounded-full bg-n-teal-9" />
              {{ t('ONBOARDING_VISOR.AI_TEST.AI_REPLYING') }}
            </p>
          </div>
          <span class="ml-auto" :class="[CHIP, CHIP_TONES.exemplo]">
            <Icon icon="i-ph-play-circle-duotone" class="size-3.5" />
            {{ t('ONBOARDING_VISOR.AI_TEST.SIMULATION') }}
          </span>
        </div>

        <div v-if="loadingAssistant" class="p-8 grid place-items-center"><Spinner /></div>

        <div v-else-if="!assistant" class="p-6" data-testid="sem-assistente">
          <p class="text-n-slate-11">{{ t('ONBOARDING_VISOR.AI_TEST.NO_ASSISTANT') }}</p>
          <router-link :to="{ name: 'captain_assistants_index', params: { accountId } }" class="mt-4" :class="[BTN_SECONDARY, BTN_SMALL]">
            {{ t('ONBOARDING_VISOR.AI_TEST.CREATE_ASSISTANT') }}
          </router-link>
        </div>

        <template v-else>
          <div ref="conversation" class="p-5 flex flex-col gap-2.5 bg-n-background min-h-[18rem] max-h-[32rem] overflow-y-auto max-md:p-3.5" aria-live="polite">
            <p v-if="!messages.length" class="self-center text-center max-w-sm my-auto" :class="SMALL">
              {{ t('ONBOARDING_VISOR.AI_TEST.EMPTY') }}
            </p>
            <div
              v-for="(message, index) in messages"
              :key="index"
              class="flex"
              :class="message.role === 'assistant' ? 'justify-end' : ''"
              data-testid="mensagem"
            >
              <p
                v-if="message.role === 'user'"
                class="max-w-[78%] max-md:max-w-[88%] px-3.5 py-2.5 rounded-2xl rounded-bl-md bg-n-card border border-n-weak text-n-slate-12 whitespace-pre-line"
              >
                {{ message.content }}
              </p>
              <p
                v-else-if="message.error || message.handoff"
                class="max-w-[78%] px-3.5 py-2.5 rounded-2xl text-sm bg-n-amber-3 text-n-amber-11"
              >
                {{ message.handoff ? t('ONBOARDING_VISOR.AI_TEST.HANDOFF') : message.content }}
              </p>
              <p
                v-else
                class="max-w-[78%] max-md:max-w-[88%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-white whitespace-pre-line"
                :class="brandColor ? '' : 'bg-n-blue-9'"
                :style="brandColor ? { background: brandColor } : {}"
              >
                {{ message.content }}
              </p>
            </div>
            <div v-if="thinking" class="flex justify-end">
              <span class="px-3.5 py-2.5 rounded-2xl bg-n-card border border-n-weak" :class="SMALL">
                {{ t('ONBOARDING_VISOR.AI_TEST.THINKING') }}
              </span>
            </div>
          </div>
          <div class="flex gap-2 items-center flex-wrap px-5 pt-3.5">
            <span :class="SMALL">{{ t('ONBOARDING_VISOR.AI_TEST.ASK_ANOTHER') }}</span>
            <button
              v-for="key in SUGGESTIONS"
              :key="key"
              type="button"
              class="h-8 px-2.5 rounded-md text-[0.8125rem] bg-n-blue-3 text-n-blue-11 hover:bg-n-blue-4"
              :disabled="thinking"
              @click="send(t(`ONBOARDING_VISOR.AI_TEST.SUGGESTIONS.${key}`))"
            >
              {{ t(`ONBOARDING_VISOR.AI_TEST.SUGGESTIONS.${key}`) }}
            </button>
          </div>
          <form class="flex gap-2.5 px-5 pt-3.5 pb-5" @submit.prevent="send()">
            <input
              v-model="draft"
              :class="FIELD"
              class="flex-1 min-w-0 !mb-0"
              :placeholder="t('ONBOARDING_VISOR.AI_TEST.PLACEHOLDER')"
              data-testid="entrada"
            />
            <button type="submit" :class="BTN" class="!w-12 !px-0" :disabled="thinking || !draft.trim()" :aria-label="t('ONBOARDING_VISOR.AI_TEST.SEND')">
              <Icon icon="i-ph-arrow-right-duotone" class="size-5" />
            </button>
          </form>
        </template>
      </section>

      <div class="grid gap-6 max-md:gap-4">
        <section v-if="answered" :class="CARD" class="!bg-n-teal-3 !border-transparent" data-testid="vitoria">
          <span class="size-12 rounded-xl grid place-items-center bg-n-card text-n-teal-11 mb-3.5">
            <Icon icon="i-ph-trophy-duotone" class="size-[1.625rem]" />
          </span>
          <h2 class="text-xl font-medium tracking-[-0.02em]">{{ t('ONBOARDING_VISOR.AI_TEST.WIN_TITLE') }}</h2>
          <p class="text-n-slate-11 mt-2">{{ t('ONBOARDING_VISOR.AI_TEST.WIN_TEXT') }}</p>
        </section>
        <section v-if="toolsUsed.length" :class="CARD">
          <div class="flex items-center gap-3 mb-3">
            <span :class="[TILE, TILE_TONES.azul]"><Icon icon="i-ph-eye-duotone" /></span>
            <h3 class="font-medium">{{ t('ONBOARDING_VISOR.AI_TEST.USED') }}</h3>
          </div>
          <ul class="grid">
            <li v-for="tool in toolsUsed" :key="tool" class="flex gap-3 items-center py-3 border-t border-n-weak first:border-t-0 first:pt-0 text-n-slate-11">
              <Icon icon="i-ph-magic-wand-duotone" class="size-5 text-n-slate-10" />
              {{ tool }}
            </li>
          </ul>
        </section>
        <DicaDestaque :title="t('ONBOARDING_VISOR.AI_TEST.TIPS_TITLE')" :tips="tips" />
      </div>
    </div>

    <AcoesPasso :trust="t('ONBOARDING_VISOR.AI_TEST.TRUST')">
      <button type="button" :class="BTN" data-testid="seguir" @click="emit('next')">
        {{ t('ONBOARDING_VISOR.AI_TEST.NEXT') }}
        <Icon icon="i-ph-arrow-right-duotone" class="size-5" />
      </button>
      <button v-if="messages.length" type="button" :class="BTN_SECONDARY" class="max-md:hidden" @click="restart">
        {{ t('ONBOARDING_VISOR.AI_TEST.RESTART') }}
      </button>
    </AcoesPasso>
  </div>
</template>
